import { useCallback, useEffect, useId, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import { denialReason, type Ability } from '@/lib/permissions';
import type {
  B2bProposalRoutingMatchField,
  B2bProposalRoutingRule,
  B2bProposalRoutingSettings,
  B2bProposalRoutingUpdateInput,
} from '@/lib/types';
import {
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Panel,
  PanelBody,
  PanelHeader,
  Select,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * ROUTING RULES FOR B2B "GET IN TOUCH" PROPOSAL NOTICES.
 *
 * Read and written through `GET | PUT /settings/b2b-proposal-routing`. A rule
 * ADDS its recipients to the {@link B2bProposalRecipientsCard} list when a
 * proposal's country, country code, continent or geography of focus matches
 * (case-insensitive,
 * EXACT — never a substring). It never replaces that list.
 *
 * ## 🔴 THE WRITE IS A WHOLE-SET REPLACE, AND ORDER IS STORED
 *
 * The PUT body is every rule, in order; the array position IS the stored sort
 * order. So:
 *
 * - **Save sends the whole draft**, never a diff — a rule missing from the body
 *   is a rule deleted.
 * - **"Has anything changed" is compared ORDER-SENSITIVELY** against the loaded
 *   set, because a reordering is a real edit to a stored field.
 * - **`rules: []` removes every rule**, so reaching it is a named action behind
 *   `ConfirmDialog` — never an emptied list plus Save. Same ruling as the
 *   recipients card's "Remove everyone".
 *
 * ## Admin tier on the READ as well as the write
 *
 * A non-admin gets a `403` on the GET, rendered as a refusal about their
 * account with NO controls — never as an empty list, which would claim the
 * business has no rules.
 */

/** Server caps. ⚠️ Read from the backend's settings schema, not chosen here. */
const MAX_RULES = 100;
const MAX_RECIPIENTS_PER_RULE = 25;
const MAX_ADDRESS_LENGTH = 255;

/** The top-level path the server names for a refusal about the whole array. */
const RULES_KEY = 'rules';

const MATCH_FIELD_OPTIONS: readonly { value: B2bProposalRoutingMatchField; label: string }[] = [
  { value: 'country', label: 'Country' },
  { value: 'countryCode', label: 'Country code' },
  { value: 'continent', label: 'Continent' },
  { value: 'geography', label: 'Geography of focus' },
];


/*
 * Row identity is a KEY, never an index — rules and addresses are added and
 * removed under a person who is typing, and a per-address refusal has to be
 * mapped back after blanks are dropped from the payload.
 */
let nextKey = 0;
function newKey(prefix: string): string {
  nextKey += 1;
  return `b2b-routing-${prefix}-${nextKey}`;
}

interface RecipientRow {
  key: string;
  value: string;
}

interface RuleRow {
  key: string;
  matchField: B2bProposalRoutingMatchField;
  matchValue: string;
  recipients: RecipientRow[];
  isActive: boolean;
}

function rowsFrom(rules: readonly B2bProposalRoutingRule[]): RuleRow[] {
  return rules.map((rule) => ({
    key: newKey('rule'),
    matchField: rule.matchField,
    matchValue: rule.matchValue,
    recipients: rule.recipients.map((address) => ({ key: newKey('addr'), value: address })),
    isActive: rule.isActive,
  }));
}

function blankRule(): RuleRow {
  return {
    key: newKey('rule'),
    matchField: 'country',
    matchValue: '',
    recipients: [{ key: newKey('addr'), value: '' }],
    isActive: true,
  };
}

/**
 * What a save would send, each address still carrying the row it came from.
 *
 * 🔴 **ONE COMPUTATION FOR THE BODY AND THE ERROR MAP.** The server's
 * `rules.N.recipients.M` indexes into what was SENT — blanks dropped — so the
 * mapping back to a row must read the same array the body was built from, or a
 * refusal marks a perfectly good address (the `ccEmails` defect).
 *
 * ⚠️ **Rules are never dropped**, even a blank one, so `rules.N` is the draft's
 * own index. A blank rule is sent and refused by the server at its own control.
 */
interface PayloadRule {
  key: string;
  matchField: B2bProposalRoutingMatchField;
  matchValue: string;
  recipients: { key: string; address: string }[];
  isActive: boolean;
}

function payloadOf(rows: readonly RuleRow[]): PayloadRule[] {
  return rows.map((row) => ({
    key: row.key,
    matchField: row.matchField,
    matchValue: row.matchValue.trim(),
    recipients: row.recipients
      .map((recipient) => ({ key: recipient.key, address: recipient.value.trim() }))
      .filter((entry) => entry.address !== ''),
    isActive: row.isActive,
  }));
}

function bodyOf(payload: readonly PayloadRule[]): B2bProposalRoutingUpdateInput {
  return {
    rules: payload.map((rule) => ({
      matchField: rule.matchField,
      matchValue: rule.matchValue,
      recipients: rule.recipients.map((entry) => entry.address),
      isActive: rule.isActive,
    })),
  };
}

/**
 * ORDER-SENSITIVE: the array position is a stored field, so a reorder is an
 * edit. ⚠️ Compares against the trimmed payload; a case-only change counts as a
 * change even though the server lowercases, because a button wrongly enabled
 * costs one request where one wrongly disabled is a false statement.
 */
function sameAsStored(
  payload: readonly PayloadRule[],
  stored: readonly B2bProposalRoutingRule[],
): boolean {
  if (payload.length !== stored.length) return false;
  return payload.every((rule, index) => {
    const saved = stored[index];
    if (!saved) return false;
    return (
      rule.matchField === saved.matchField &&
      rule.matchValue === saved.matchValue &&
      rule.isActive === saved.isActive &&
      rule.recipients.length === saved.recipients.length &&
      rule.recipients.every((entry, at) => entry.address === saved.recipients[at])
    );
  });
}

/** Per-control errors, keyed by row KEY (and address key), never by index. */
interface RuleErrors {
  matchField?: string;
  matchValue?: string;
  isActive?: string;
  /** A refusal about the rule's recipient list as a whole. */
  recipients?: string;
  /** Keyed by the address row's key. */
  addresses: Record<string, string>;
}

/**
 * The sentence a reader who may not write sees. Ability object, never the role
 * directly — the admin tier is the floor and the permission narrows it, the
 * order every card on this page states.
 */
function routingDenialFor(ability: Ability): string | null {
  // 2026-10-01: the set decides for every role. *SUPERSEDED: admin tier first.*
  // 2026-10-02 (owner): recipients and routing are `settings-notifications`.
  return denialReason(ability, 'update', 'settings-notifications');
}

export function B2bProposalRoutingCard() {
  const { ability } = useAuth();
  const headingId = useId();

  const [settings, setSettings] = useState<B2bProposalRoutingSettings | null>(null);
  const [rows, setRows] = useState<RuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [ruleErrors, setRuleErrors] = useState<Record<string, RuleErrors>>({});
  const [listError, setListError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<'rules' | 'cleared' | null>(null);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  const load = useCallback((signal: AbortSignal) => {
    return api
      .getB2bProposalRouting(signal)
      .then((response) => {
        if (signal.aborted) return;
        setSettings(response.data);
        setRows(rowsFrom(response.data.rules));
        setError(null);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        // A 403 is a refusal about this reader, never a failure and never an
        // empty rule list. A 401 is `@/lib/api`'s to handle.
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(true);
          return;
        }
        if (caught instanceof ApiError && caught.status === 401) return;
        setError({
          title: 'Couldn’t read the routing rules.',
          message:
            caught instanceof Error
              ? caught.message
              : 'The rules could not be read, so what is on screen may not be what is stored.',
        });
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const denial = routingDenialFor(ability);
  const payload = payloadOf(rows);
  const storedRules = settings?.rules ?? [];
  const storedIsEmpty = settings !== null && settings.rules.length === 0;
  const unchanged = settings !== null && sameAsStored(payload, storedRules);

  function clearMessages() {
    setSaved(null);
    setListError(null);
  }

  function applySaved(next: B2bProposalRoutingSettings) {
    setSettings(next);
    // Re-seeded from the RESPONSE: the server trims and lowercases, so the
    // card shows what is STORED rather than what was typed.
    setRows(rowsFrom(next.rules));
    setRuleErrors({});
    setListError(null);
    setSaved(next.rules.length === 0 ? 'cleared' : 'rules');
  }

  /**
   * Maps `rules.N.matchValue`, `rules.N.recipients.M` and friends onto the
   * control they name. Every path is built from `sent`, so the indexes cannot
   * disagree with the rows they mark; anything unmatched goes to the banner.
   */
  function handleWriteFailure(caught: unknown, sent: readonly PayloadRule[], title: string) {
    if (!(caught instanceof ApiError)) {
      setError({
        title,
        message: caught instanceof Error ? caught.message : 'The rules were not saved.',
      });
      return;
    }

    const known: string[] = [RULES_KEY];
    sent.forEach((rule, index) => {
      const base = `${RULES_KEY}.${index}`;
      known.push(
        base,
        `${base}.matchField`,
        `${base}.matchValue`,
        `${base}.isActive`,
        `${base}.recipients`,
      );
      rule.recipients.forEach((_entry, at) => known.push(`${base}.recipients.${at}`));
    });
    const routed = caught.routeFieldErrors(known);

    const byRule: Record<string, RuleErrors> = {};
    const unroutable = [...routed.unroutable];
    let list: string | null = null;

    for (const [path, message] of Object.entries(routed.fields)) {
      if (path === RULES_KEY) {
        list = message;
        continue;
      }
      const parts = path.split('.');
      const rule = sent[Number(parts[1])];
      if (!rule) {
        unroutable.push(`${path}: ${message}`);
        continue;
      }
      const entry = (byRule[rule.key] ??= { addresses: {} });
      const field = parts[2];
      if (field === undefined) {
        // `rules.N` alone — about the rule as a whole; shown on its value.
        entry.matchValue = entry.matchValue ?? message;
      } else if (field === 'recipients' && parts[3] !== undefined) {
        const address = rule.recipients[Number(parts[3])];
        if (address) entry.addresses[address.key] = message;
        else unroutable.push(`${path}: ${message}`);
      } else if (field === 'recipients') {
        entry.recipients = message;
      } else if (field === 'matchField' || field === 'matchValue' || field === 'isActive') {
        entry[field] = message;
      } else {
        unroutable.push(`${path}: ${message}`);
      }
    }

    setRuleErrors(byRule);
    setListError(list);
    if (unroutable.length > 0) {
      setError({ title, message: unroutable.join(' ') });
    } else if (list === null && Object.keys(byRule).length === 0) {
      // A 403 carries no `details`, so there is nothing to route.
      setError({ title, message: caught.message });
    }
  }

  function save() {
    if (saving || payload.length === 0 || unchanged) return;
    const sent = payload;
    setSaving(true);
    setSaved(null);
    setError(null);
    setRuleErrors({});
    setListError(null);
    api
      .setB2bProposalRouting(bodyOf(sent))
      .then((response) => applySaved(response.data))
      .catch((caught: unknown) =>
        handleWriteFailure(caught, sent, 'Couldn’t save the routing rules.'),
      )
      .finally(() => setSaving(false));
  }

  /** `rules: []` — deliberate, confirmed, and reported inside the dialog. */
  function clearAll() {
    if (saving) return;
    setSaving(true);
    setSaved(null);
    setError(null);
    setClearError(null);
    setRuleErrors({});
    setListError(null);
    api
      .setB2bProposalRouting({ rules: [] })
      .then((response) => {
        applySaved(response.data);
        setConfirmingClear(false);
      })
      .catch((caught: unknown) => {
        if (caught instanceof ApiError) {
          const { unroutable } = caught.routeFieldErrors([]);
          setClearError(unroutable.length > 0 ? unroutable.join(' ') : caught.message);
          return;
        }
        setClearError(caught instanceof Error ? caught.message : 'The rules were not changed.');
      })
      .finally(() => setSaving(false));
  }

  function dropRuleError(ruleKey: string, update: (current: RuleErrors) => RuleErrors) {
    setRuleErrors((current) => {
      const existing = current[ruleKey];
      if (!existing) return current;
      return { ...current, [ruleKey]: update(existing) };
    });
  }

  function updateRule(ruleKey: string, patch: Partial<Omit<RuleRow, 'key' | 'recipients'>>) {
    clearMessages();
    dropRuleError(ruleKey, (current) => {
      const next = { ...current };
      for (const field of Object.keys(patch) as (keyof typeof patch)[]) delete next[field];
      return next;
    });
    setRows((current) => current.map((row) => (row.key === ruleKey ? { ...row, ...patch } : row)));
  }

  function removeRule(ruleKey: string) {
    clearMessages();
    setRuleErrors((current) => {
      if (!(ruleKey in current)) return current;
      const next = { ...current };
      delete next[ruleKey];
      return next;
    });
    setRows((current) => current.filter((row) => row.key !== ruleKey));
  }

  function addRule() {
    clearMessages();
    setRows((current) => [...current, blankRule()]);
  }

  function updateAddress(ruleKey: string, addressKey: string, value: string) {
    clearMessages();
    dropRuleError(ruleKey, (current) => {
      const addresses = { ...current.addresses };
      delete addresses[addressKey];
      const next: RuleErrors = { ...current, addresses };
      delete next.recipients;
      return next;
    });
    setRows((current) =>
      current.map((row) =>
        row.key === ruleKey
          ? {
              ...row,
              recipients: row.recipients.map((recipient) =>
                recipient.key === addressKey ? { ...recipient, value } : recipient,
              ),
            }
          : row,
      ),
    );
  }

  function addAddress(ruleKey: string) {
    clearMessages();
    setRows((current) =>
      current.map((row) =>
        row.key === ruleKey
          ? { ...row, recipients: [...row.recipients, { key: newKey('addr'), value: '' }] }
          : row,
      ),
    );
  }

  function removeAddress(ruleKey: string, addressKey: string) {
    clearMessages();
    dropRuleError(ruleKey, (current) => {
      const addresses = { ...current.addresses };
      delete addresses[addressKey];
      return { ...current, addresses };
    });
    setRows((current) =>
      current.map((row) =>
        row.key === ruleKey
          ? { ...row, recipients: row.recipients.filter((r) => r.key !== addressKey) }
          : row,
      ),
    );
  }

  const locked = denial !== null || saving;
  const atRuleCap = rows.length >= MAX_RULES;
  const saveDisabled = locked || payload.length === 0 || unchanged;
  /*
   * The "Remove all rules" pointer appears only while that control is on
   * screen — naming a control the reader cannot reach reads as a bug.
   */
  const saveReason =
    denial ??
    (payload.length === 0
      ? storedIsEmpty
        ? 'Add a rule to save.'
        : 'To leave no rules, use “Remove all rules”.'
      : unchanged
        ? 'Nothing has changed.'
        : null);

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader
        id={headingId}
        title="B2B proposal routing"
        /*
         * ⚠️ The one sentence this card carries, and it is the documented
         * exception to the no-helper-text rule: it states behaviour a reader
         * cannot see — that a rule ADDS to the list above rather than replacing
         * it. ⛔ Keep it to this one line.
         */
        description="Adds recipients when a proposal’s country, country code, continent or geography of focus matches. Additive to the list above."
      />
      <PanelBody className="flex flex-col gap-4">
        {error && (
          <Notice tone="danger" role="alert" title={error.title}>
            <span className="min-w-0 [overflow-wrap:anywhere]">{error.message}</span>
          </Notice>
        )}

        {loading && settings === null && !forbidden ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-28" />
          </div>
        ) : forbidden ? (
          <p className="m-0 text-xs text-fg-subtle">
            Your permission set does not include this. An administrator can change that.
          </p>
        ) : settings === null ? null : (
          <>
            {rows.length === 0 ? (
              <EmptyState
                title={storedIsEmpty ? 'No routing rules' : 'Every rule has been taken out'}
                description={
                  storedIsEmpty
                    ? 'Every proposal goes to the list above.'
                    : 'Nothing is saved until you add a rule. The stored rules are unchanged.'
                }
                action={
                  <Button onClick={addRule} disabled={locked}>
                    Add rule
                  </Button>
                }
              />
            ) : (
              <>
                <ol className="m-0 flex list-none flex-col gap-3 p-0">
                  {rows.map((row, index) => {
                    const errors = ruleErrors[row.key];
                    const atAddressCap = row.recipients.length >= MAX_RECIPIENTS_PER_RULE;
                    return (
                      <li
                        key={row.key}
                        className="flex flex-col gap-3 rounded-md border border-border p-3"
                        aria-label={`Rule ${index + 1}`}
                      >
                        <div className="grid gap-3 sm:grid-cols-[11rem_minmax(0,1fr)]">
                          <Field label="Match on" error={errors?.matchField}>
                            {({ id, 'aria-describedby': describedBy }) => (
                              <Select
                                id={id}
                                value={row.matchField}
                                disabled={locked}
                                invalid={Boolean(errors?.matchField)}
                                aria-describedby={describedBy}
                                onChange={(event) =>
                                  updateRule(row.key, {
                                    matchField: event.target.value as B2bProposalRoutingMatchField,
                                  })
                                }
                              >
                                {MATCH_FIELD_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </Select>
                            )}
                          </Field>
                          <Field label="Value" error={errors?.matchValue}>
                            {({ id, 'aria-describedby': describedBy }) => (
                              <Input
                                id={id}
                                value={row.matchValue}
                                spellCheck={false}
                                disabled={locked}
                                invalid={Boolean(errors?.matchValue)}
                                aria-describedby={describedBy}
                                onChange={(event) =>
                                  updateRule(row.key, { matchValue: event.target.value })
                                }
                              />
                            )}
                          </Field>
                        </div>

                        <div className="flex flex-col gap-2">
                          <p className="m-0 text-xs font-medium text-fg-secondary">Recipients</p>
                          {row.recipients.map((recipient, at) => (
                            <div key={recipient.key} className="flex items-start gap-2">
                              <div className="min-w-0 flex-1">
                                <Field
                                  label={`Rule ${index + 1} recipient ${at + 1}`}
                                  hideLabel
                                  error={errors?.addresses[recipient.key]}
                                >
                                  {({ id, 'aria-describedby': describedBy }) => (
                                    <Input
                                      id={id}
                                      type="email"
                                      inputMode="email"
                                      spellCheck={false}
                                      value={recipient.value}
                                      maxLength={MAX_ADDRESS_LENGTH}
                                      disabled={locked}
                                      invalid={Boolean(errors?.addresses[recipient.key])}
                                      aria-describedby={describedBy}
                                      onChange={(event) =>
                                        updateAddress(row.key, recipient.key, event.target.value)
                                      }
                                    />
                                  )}
                                </Field>
                              </div>
                              <Button
                                variant="ghost"
                                onClick={() => removeAddress(row.key, recipient.key)}
                                disabled={locked}
                                aria-label={`Remove rule ${index + 1} recipient ${at + 1}`}
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                          {errors?.recipients && (
                            <p role="alert" className="m-0 text-xs text-danger">
                              {errors.recipients}
                            </p>
                          )}
                          <span
                            className="self-start"
                            title={
                              atAddressCap
                                ? `A rule holds at most ${MAX_RECIPIENTS_PER_RULE} addresses.`
                                : undefined
                            }
                          >
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => addAddress(row.key)}
                              disabled={locked || atAddressCap}
                            >
                              Add address
                            </Button>
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                          <Checkbox
                            label="Active"
                            checked={row.isActive}
                            disabled={locked}
                            onChange={(event) =>
                              updateRule(row.key, { isActive: event.target.checked })
                            }
                          />
                          <Button
                            variant="ghost"
                            onClick={() => removeRule(row.key)}
                            disabled={locked}
                            aria-label={`Remove rule ${index + 1}`}
                          >
                            Remove rule
                          </Button>
                        </div>
                        {errors?.isActive && (
                          <p role="alert" className="m-0 text-xs text-danger">
                            {errors.isActive}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ol>

                {listError && (
                  <p role="alert" className="m-0 text-xs text-danger">
                    {listError}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  {/* ⚠️ `title` on a WRAPPER: a disabled `Button` never gets the hover. */}
                  <span title={atRuleCap ? `At most ${MAX_RULES} rules.` : undefined}>
                    <Button variant="secondary" onClick={addRule} disabled={locked || atRuleCap}>
                      Add rule
                    </Button>
                  </span>
                  {atRuleCap && (
                    <p className="m-0 text-xs text-fg-subtle">{`At most ${MAX_RULES} rules.`}</p>
                  )}
                </div>
              </>
            )}

            <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
              <span title={saveReason ?? undefined}>
                <Button onClick={save} disabled={saveDisabled}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </span>
              {saveReason && <p className="m-0 text-xs text-fg-subtle">{saveReason}</p>}
              {saved && !denial && (
                <p role="status" className="m-0 text-xs text-fg-subtle">
                  {saved === 'cleared' ? 'Saved. There are no routing rules.' : 'Saved.'}
                </p>
              )}
              {!storedIsEmpty && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setClearError(null);
                    setConfirmingClear(true);
                  }}
                  disabled={locked}
                >
                  Remove all rules
                </Button>
              )}
            </div>
          </>
        )}
      </PanelBody>

      <ConfirmDialog
        open={confirmingClear}
        onClose={() => setConfirmingClear(false)}
        onConfirm={clearAll}
        title="Remove every routing rule?"
        description="No proposal will add recipients by country, country code, continent or geography of focus. Every proposal will go only to the list above."
        confirmLabel="Remove all rules"
        tone="danger"
        loading={saving}
        error={clearError}
      />
    </Panel>
  );
}
